import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Vérification du paiement...');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Session de paiement non trouvée');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/professional/login');
          return;
        }

        const response = await axios.get(
          `${API}/payments/status/${sessionId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setMessage('Paiement confirmé avec succès!');
        } else if (response.data.status === 'expired') {
          setStatus('error');
          setMessage('La session de paiement a expiré');
        } else if (attempts < maxAttempts) {
          // Continue polling
          setMessage('Traitement du paiement en cours...');
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Délai de vérification dépassé. Veuillez vérifier votre email pour la confirmation.');
        }
      } catch (err) {
        console.error('Payment status check error:', err);
        if (attempts < maxAttempts) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Erreur lors de la vérification du paiement');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, navigate, attempts, maxAttempts]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 pt-32 pb-20">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              {status === 'checking' && (
                <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle className="h-16 w-16 text-green-500" />
              )}
              {status === 'error' && (
                <AlertCircle className="h-16 w-16 text-red-500" />
              )}
            </div>
            <CardTitle className="text-3xl text-white text-center">
              {status === 'checking' && 'Vérification du paiement'}
              {status === 'success' && 'Paiement réussi!'}
              {status === 'error' && 'Erreur de paiement'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert 
              className={
                status === 'success' 
                  ? 'bg-green-900/20 border-green-700'
                  : status === 'error'
                  ? 'bg-red-900/20 border-red-700'
                  : 'bg-blue-900/20 border-blue-700'
              }
            >
              <AlertDescription className="text-center text-lg">
                {message}
              </AlertDescription>
            </Alert>

            {status === 'success' && (
              <div className="space-y-4 text-gray-300 text-center">
                <p>
                  Votre abonnement a été activé avec succès! Vous avez maintenant accès à tous les avantages de votre plan.
                </p>
                <p className="text-sm text-gray-400">
                  Un email de confirmation vous a été envoyé avec les détails de votre abonnement.
                </p>
              </div>
            )}

            {status === 'checking' && (
              <div className="text-center text-gray-400">
                <p>Tentative {attempts + 1} sur {maxAttempts}</p>
                <p className="text-sm mt-2">Veuillez patienter, nous vérifions votre paiement...</p>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              {status === 'success' && (
                <Button
                  onClick={() => navigate('/professional/dashboard')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Retour au Dashboard
                </Button>
              )}
              {status === 'error' && (
                <>
                  <Button
                    onClick={() => navigate('/professional/dashboard')}
                    variant="outline"
                    className="border-slate-600 text-gray-300 hover:bg-slate-700"
                  >
                    Retour au Dashboard
                  </Button>
                  <Button
                    onClick={() => window.location.reload()}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Réessayer
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;
